import { AIChatUI } from './components/AIChat.js';
import './styles/app.css';
import { stateManager } from './services/state.js';
import { BottomNav } from './components/BottomNav.js';
import { authManager } from './services/auth.js';
import { registerSW } from 'virtual:pwa-register';
import { scheduleIdleMapPreload } from './services/mapPrefetch.js';
import { FEATURES } from './config/features.js';

// Trigger background map preloading on idle
scheduleIdleMapPreload();

// --- PWA & Connectivity ---
let refreshing = false;
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    const hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (hadController && !refreshing) {
            refreshing = true;
            window.location.reload();
        }
    });
}

const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
        // Auto update without requiring manual confirmation dialog prompt
        updateSW(true);
    },
    onOfflineReady() {
        console.log("App pronta per l'uso offline!");
    },
});

window.addEventListener('online', () => {
    document.body.classList.remove('is-offline');
    stateManager.syncOnReconnect().catch(e => console.warn("Auto-sync on reconnect failed:", e));
});
window.addEventListener('offline', () => document.body.classList.add('is-offline'));

window.addEventListener('navigate', (e) => {
    if (e.detail) {
        if (typeof e.detail === 'string') {
            navigateTo(e.detail);
        } else if (e.detail.page) {
            navigateTo(e.detail.page, e.detail.params || {});
        }
    }
});

const app = document.querySelector('#app');
let currentNavId = 0;

async function navigateTo(originalPage, params = {}, pushToHistory = true) {
    const navId = ++currentNavId;
    let component;
    let showNav = true;
    let page = originalPage;

    // Route Guards for Guests
    const authRoutes = ['home', 'map', 'profile', 'scan', 'level', 'arberia-blog', 'voci-arberia', 'atlante-detail', 'voci-arberia-detail', 'minigame', 'world3d'];
    if (authRoutes.includes(page) && !stateManager.isAuthenticated()) {
        page = 'login'; // Redirect guests to login
        showNav = false;
    }
    
    // Route Guard per utenti autenticati ma senza B&B selezionato
    if (authRoutes.includes(page) && stateManager.isAuthenticated() && !stateManager.getSelectedBnB()) {
        page = 'select-bnb';
        showNav = false;
    }

    // Admin Route Guard
    if (page === 'dashboard' && !authManager.isAuthenticated) {
        page = 'admin-login';
        showNav = false;
    }

    try {
        switch (page) {
            case 'login':
                component = (await import('./pages/login.js')).renderLogin(navigateTo);
                showNav = false;
                break;
            case 'admin-login':
                component = (await import('./pages/admin/login.js')).renderAdminLogin(navigateTo);
                showNav = false;
                break;
            case 'dashboard':
                component = (await import('./pages/admin/dashboard.js')).renderDashboard(navigateTo);
                showNav = false;
                break;
            case 'select-bnb':
                component = (await import('./pages/select-bnb.js')).renderSelectBnB(navigateTo);
                showNav = false;
                break;
            case 'select-route':
                component = (await import('./pages/select-route.js')).renderSelectRoute(navigateTo);
                showNav = false;
                break;
            case 'privacy':
                component = (await import('./pages/privacy.js')).renderPrivacy(navigateTo);
                showNav = false;
                break;
            case 'bnb-partners':
                component = (await import('./pages/bnb-partners.js')).renderBnbPartners(navigateTo);
                showNav = false;
                break;
            case 'marketplace':
                component = (await import('./pages/marketplace.js')).renderMarketplace(navigateTo);
                showNav = false;
                break;
            case 'voci-arberia':
                component = (await import('./pages/voci-arberia.js')).renderVociArberia(navigateTo);
                showNav = true; // Show nav for this list
                break;
            case 'atlante-detail':
                component = (await import('./pages/atlante-detail.js')).renderAtlanteDetail(navigateTo, params);
                showNav = false; // Hide nav for immersive reading/playing
                break;
            case 'voci-arberia-detail':
                component = (await import('./pages/story-detail.js')).renderStoryDetail(navigateTo, params);
                showNav = false; // Hide nav for immersive reading? Or keep it? Let's hide bottom nav to focus.
                break;
            case 'minigame':
                component = (await import('./pages/minigame.js')).renderMinigame(navigateTo, params);
                showNav = false;
                break;
            case 'arberia-blog':
                component = (await import('./pages/arberia-blog.js')).renderArberiaBlog(navigateTo, params);
                showNav = true; // Show nav for blog
                break;
            case 'guide':
                component = (await import('./pages/guide-hub.js')).renderGuideHub(navigateTo);
                break;
            case 'home':
                component = (await import('./pages/home.js')).renderHome(navigateTo);
                break;
            case 'map':
                component = (await import('./pages/map.js')).renderMapPage(navigateTo);
                break;
            case 'world3d':
                if (!FEATURES.WORLD_3D_ENABLED) {
                    component = (await import('./pages/map.js')).renderMapPage(navigateTo);
                } else {
                    const { renderWorld3DView } = await import('./components/World3DView.js');
                    component = renderWorld3DView(navigateTo);
                    showNav = false; // Hide BottomNav in 3D
                }
                break;
            case 'profile':
                component = (await import('./pages/profile.js')).renderProfile(navigateTo);
                break;
            case 'scan':
                component = (await import('./pages/scan.js')).renderScan(navigateTo, params);
                showNav = false;
                break;
            case 'level':
                component = (await import('./pages/level.js')).renderLevel(navigateTo, params);
                showNav = false;
                break;
            case 'landing':
            default:
                component = (await import('./pages/login.js')).renderLogin(navigateTo);
                showNav = false;
        }

        // Discard stale async navigation
        if (navId !== currentNavId) return;

        if (component) {
            let url = (page === 'login' && window.location.pathname === '/') ? '/' : `/${page}`;
            if (params && Object.keys(params).length > 0) {
                const searchParams = new URLSearchParams(params);
                url += `?${searchParams.toString()}`;
            }

            if (pushToHistory) {
                window.history.pushState({ page, params }, '', url);
            } else if (originalPage !== page) {
                // Redirect occurred during a non-push navigation
                window.history.replaceState({ page, params }, '', url);
            } else if (!window.history.state) {
                // Initial load needs a state
                window.history.replaceState({ page, params }, '', url);
            }

            // Cleanly replace app content when the new component is ready
            app.innerHTML = '';

            if (showNav) {
                const layout = document.createElement('div');
                layout.className = "flex flex-col h-full bg-brand-bg";

                const content = document.createElement('div');
                content.className = "flex-1 overflow-hidden relative";
                content.appendChild(component);

                layout.appendChild(content);
                layout.appendChild(BottomNav(page, navigateTo));

                app.appendChild(layout);
            } else {
                app.appendChild(component);
            }
        }
    } catch (error) {
        console.error("Navigation Error:", error);
        app.innerHTML = `<div class="p-8 text-red-500 font-bold">Errore di Navigazione:<br>${error.message}<br><small>${error.stack}</small></div>`;
    }
}

// Handle Browser Back/Forward buttons
window.addEventListener('popstate', (event) => {
    if (event.state) {
        navigateTo(event.state.page, event.state.params, false);
    } else {
        const rawPath = window.location.pathname.substring(1);
        const path = (rawPath === '' || rawPath === 'landing') ? 'login' : rawPath;
        const searchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(searchParams.entries());
        navigateTo(path, params, false);
    }
});

import { dataManager } from './services/data.js';
import { initAnalytics } from './services/firebase.js';
import { AIBanner } from './components/AIBanner.js';
import { geoManager } from './services/geo.js';

// Initial Route
async function init() {
    try {
        // Show Loading...
        app.innerHTML = '<div class="h-full w-full flex items-center justify-center bg-brand-bg text-brand-primary font-bold animate-pulse">Caricamento Arberia...</div>';

        const rawPath = window.location.pathname.substring(1);
        const path = (rawPath === '' || rawPath === 'landing') ? 'login' : rawPath;
        const searchParams = new URLSearchParams(window.location.search);
        const initialParams = Object.fromEntries(searchParams.entries());

        if (stateManager.isAuthenticated()) {
            await dataManager.init();
            // Prompt GPS immediately on access
            setTimeout(() => geoManager.promptLocationAccess(), 400);

            if (path === 'login' || path === 'landing') {
                navigateTo('home', {}, false);
            } else {
                navigateTo(path, initialParams, false);
            }
        } else if (authManager.isAuthenticated) {
            const user = authManager.getCurrentUser();
            const adminCode = "ADMIN_" + (user?.email?.split('@')[0].toUpperCase() || "USER");
            
            // Use local state if already present, but inject identifying code
            stateManager.state.guestCode = adminCode;
            
            // Initialize Sync to admin_progress collection
            await stateManager.initAdminSync(user);
            
            await dataManager.init();
            if (path === 'login' || path === 'landing' || path === 'admin-login') {
                navigateTo('select-bnb', {}, false);
            } else {
                navigateTo(path, initialParams, false);
            }
        } else {
            // Avvio rapido, init in background per Firebase
            dataManager.init().catch(e => console.error("Background data init error:", e));
            navigateTo(path, initialParams, false);
        }

        // Defer Analytics and non-critical trackers to reduce unused JS on boot
        setTimeout(() => {
            initAnalytics();
            console.log("Analytics initialized (deferred)");
            
            const banner = AIBanner();
            if (banner) document.body.appendChild(banner);
        }, 2000);
    } catch (e) {
        console.error("BOOT ERROR:", e);
        app.innerHTML = `<div class="p-8 text-red-500 font-bold">Errore di Caricamento:<br>${e.message}<br><small>${e.stack}</small></div>`;
    }
}

init();

// Inizializza l'interfaccia AI Globale
window.arberiaAI = new AIChatUI();


window.addEventListener('arberia-ai-action', (e) => {
    console.log('AI Action received:', e.detail);
    // In futuro potrai mappare 'SHOW_POI' verso un cambio pagina (es. navigateTo('atlante-detail'))
});

